import { useMemo, useCallback } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { useToast } from '@/hooks/errors/use-toast';
import { useDebouncedValue } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useResearchContext } from '@/contexts/research/ResearchContext';
import type { PrivateResearchItem, PrivateResearchFilter, UsePrivateResearchLibraryDataProps } from './types';

/**
 * Enhanced hook for private research library data with sophisticated filtering
 * Supports both simple filters (backward compatibility) and complex ResearchContext integration
 */
export const usePrivateResearchLibraryData = (props?: UsePrivateResearchLibraryDataProps | PrivateResearchFilter) => {
  const { user } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Get research context for advanced filtering (like Public Research)
  const { state } = useResearchContext();
  
  // Handle both old interface (PrivateResearchFilter) and new interface (UsePrivateResearchLibraryDataProps)
  const isNewInterface = props && ('showFavorites' in props || 'projectId' in props);
  
  let searchQuery: string | undefined;
  let showFavorites = false;
  let showRecent = false; 
  let showByType = false;
  let projectId: string | null | undefined;
  let filters: PrivateResearchFilter | undefined;

  if (isNewInterface) {
    // New interface (UsePrivateResearchLibraryDataProps)
    const newProps = props as UsePrivateResearchLibraryDataProps;
    searchQuery = newProps.searchQuery;
    showFavorites = newProps.showFavorites || false;
    showRecent = newProps.showRecent || false;
    showByType = newProps.showByType || false;
    projectId = newProps.projectId;
    filters = newProps.filters;
  } else if (props) {
    // Old interface (PrivateResearchFilter) - backward compatibility
    const oldProps = props as PrivateResearchFilter;
    searchQuery = oldProps.searchQuery;
    // For backward compatibility, treat any old filter as "all" scope
  }

  // FIXED: Move debounced value to top level (not inside useMemo)
  // Use researchPrompt from context as the search filter (this is how it's designed to work)
  const debouncedSearchFromContext = useDebouncedValue(state.researchPrompt || '', 500);
  const debouncedSearchFromProps = useDebouncedValue(searchQuery || '', 500);

  // Build comprehensive filter options (matching Public Research pattern)
  const filterOptions = useMemo(() => {
    // Use search from ResearchContext if available (for filtering), otherwise use props
    const debouncedSearchQuery = debouncedSearchFromContext || debouncedSearchFromProps;

    // Dynamic filters removed - no longer processing selectedFilters

    // Determine scope based on filter state
    let scope: 'all' | 'recent' | 'type' = 'all';
    if (showRecent) scope = 'recent';
    else if (showByType) scope = 'type';

    const options = {
      // Search query (debounced from ResearchContext or props)
      searchQuery: debouncedSearchQuery || undefined,
      
      // Research type from context or filters
      researchType: state.selectedType || filters?.researchType || undefined,
      
      // Advanced filters from ResearchContext (matching Public Research)
      timeframe: state.timeframe && state.timeframe !== 'none' ? state.timeframe : undefined,
      country: state.country && state.country !== 'none' ? state.country : undefined,
      sourceType: state.sourceType && state.sourceType !== 'none' ? state.sourceType : undefined,
      
      // Type-specific dynamic filters removed
      
      // Project filtering
      projectId,
      
      // Filter scope and options
      showFavorites,
      showRecent,
      showByType,
      scope,
      
      // Pagination
      pageSize: 12,
    };
    
    console.log('[usePrivateResearchLibraryData] Filter options created:', {
      projectId: options.projectId,
      projectIdUndefined: options.projectId === undefined,
      timestamp: new Date().toISOString()
    });
    
    return options;
  }, [
    debouncedSearchFromContext,
    debouncedSearchFromProps,
    state.selectedType,
    state.timeframe,
    state.country,
    state.sourceType,
    // state.selectedFilters removed
    showFavorites,
    showRecent,
    showByType,
    projectId,
    filters
  ]);

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebouncedValue(filterOptions.searchQuery || '', 300);

  // Query for private research with infinite scroll and advanced filtering
  const queryKey = ['private-research-generator', user?.id, debouncedSearchQuery, filterOptions];
  console.log('[usePrivateResearchLibraryData] Query key created:', {
    queryKey: queryKey,
    projectIdInFilterOptions: filterOptions.projectId,
    timestamp: new Date().toISOString()
  });
  
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useInfiniteQuery({
    queryKey: queryKey,
    enabled: !!user?.id,
    queryFn: async ({ pageParam = 0 }) => {
      console.log('[usePrivateResearchLibraryData] Query executing with:', {
        userId: user?.id,
        pageParam,
        filterOptions,
        timestamp: new Date().toISOString()
      });
      
      if (!user?.id) throw new Error('User not authenticated');
      
      const limit = filterOptions.pageSize || 12;
      const offset = pageParam * limit;
      
      // Build query with advanced filtering
      let query = supabase
        .from('workbook_research')
        .select('*')
        .eq('user_id', user.id)
        .is('deleted_at', null) // Only active research
        .order('created_at', { ascending: false });

      // Apply project filtering using direct workbook_id
      console.log('[usePrivateResearchLibraryData] Applying project filtering:', {
        projectId: filterOptions.projectId,
        hasProjectId: filterOptions.projectId !== undefined,
        isNull: filterOptions.projectId === null,
        timestamp: new Date().toISOString()
      });
      
      if (filterOptions.projectId !== undefined) {
        if (filterOptions.projectId === null) {
          // Show only research not assigned to any project
          console.log('[usePrivateResearchLibraryData] Filtering for NULL workbook_id (no project)');
          query = query.is('workbook_id', null);
        } else {
          // Show only research assigned to the specific project
          console.log('[usePrivateResearchLibraryData] Filtering for workbook_id =', filterOptions.projectId);
          query = query.eq('workbook_id', filterOptions.projectId);
        }
      } else {
        console.log('[usePrivateResearchLibraryData] No project filtering - showing ALL research');
      }
      // If projectId is undefined, show ALL user research

      // Apply search filter with proper escaping
      if (debouncedSearchQuery) {
        const escapedSearchQuery = debouncedSearchQuery.replace(/[%_\\]/g, '\\$&');
        query = query.or(`title.ilike.%${escapedSearchQuery}%,content.ilike.%${escapedSearchQuery}%,original_prompt.ilike.%${escapedSearchQuery}%`);
      }

      // Apply research type filter
      if (filterOptions.researchType) {
        query = query.eq('research_type', `"${filterOptions.researchType}"`);
      }

      // Apply advanced filters from research_parameters
      if (filterOptions.timeframe) {
        query = query.eq('research_parameters->>timeframe', filterOptions.timeframe);
      }

      if (filterOptions.country) {
        query = query.eq('research_parameters->>country', filterOptions.country);
      }

      if (filterOptions.sourceType) {
        query = query.eq('research_parameters->>source_type', filterOptions.sourceType);
      }

      // Apply type-specific filters (tech_domain, research_focus, etc.)
      if (filterOptions.typeSpecificFilters) {
        Object.entries(filterOptions.typeSpecificFilters).forEach(([key, value]) => {
          query = query.eq(`research_parameters->>${key}`, value);
        });
      }

      // Apply scope-based filtering
      if (filterOptions.scope === 'recent') {
        // Show items from last 7 days
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        query = query.gte('created_at', sevenDaysAgo.toISOString());
      }

      // Apply pagination
      query = query.range(offset, offset + limit - 1);

      const { data: research, error } = await query;

      if (error) {
        console.error('Error fetching private research:', error);
        throw new Error(`Failed to fetch private research: ${error.message}`);
      }
      
      const transformedResearch: PrivateResearchItem[] = (research || []).map(item => ({
        id: item.id,
        user_id: item.user_id,
        workbook_id: item.workbook_id, // Preserve actual workbook_id (null for standalone, project-id for attached)
        page_id: item.page_id,
        title: item.title,
        content: item.content,
        original_prompt: item.original_prompt,
        research_type: item.research_type,
        research_parameters: item.research_parameters,
        sources: item.sources,
        related_questions: item.related_questions,
        metadata: item.metadata,
        system_prompt: item.system_prompt,
        usage: item.usage,
        generated_research_count: item.generated_research_count || 0,
        created_at: item.created_at,
        updated_at: item.updated_at,
        deleted_at: null // Private research uses hard delete, not soft delete
      }));
      
      return {
        research: transformedResearch,
        nextPage: transformedResearch.length === limit ? pageParam + 1 : undefined,
        hasMore: transformedResearch.length === limit
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0
  });

  // Flatten paginated results
  const research = useMemo(() => {
    return data?.pages.flatMap(page => page.research) || [];
  }, [data]);

  // NEW: Query for total user research count (unfiltered) - determines hasData like Image Generator
  const { data: totalUserResearch = [] } = useQuery({
    queryKey: ['private-research-total', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('workbook_research')
        .select('id')
        .eq('user_id', user.id)
        .is('deleted_at', null)
        .limit(1); // Only need to know if ANY research exists
      
      if (error) {
        console.error('Error getting total user research:', error);
        return [];
      }
      
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - total count doesn't change often
  });

  // Query for total count with same filters (for display purposes)
  const { data: totalCount } = useQuery({
    queryKey: ['private-research-generator-count', user?.id, debouncedSearchQuery, filterOptions],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return 0;
      
      let query = supabase
        .from('workbook_research')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('deleted_at', null); // Only active research

      // Apply project filtering using direct workbook_id (same as main query)
      if (filterOptions.projectId !== undefined) {
        if (filterOptions.projectId === null) {
          // Count only research not assigned to any project
          query = query.is('workbook_id', null);
        } else {
          // Count only research assigned to the specific project
          query = query.eq('workbook_id', filterOptions.projectId);
        }
      }
      // If projectId is undefined, count ALL user research
      
      // Apply same filters as main query with proper escaping
      if (debouncedSearchQuery) {
        const escapedSearchQuery = debouncedSearchQuery.replace(/[%_\\]/g, '\\$&');
        query = query.or(`title.ilike.%${escapedSearchQuery}%,content.ilike.%${escapedSearchQuery}%,original_prompt.ilike.%${escapedSearchQuery}%`);
      }

      if (filterOptions.researchType) {
        query = query.eq('research_type', `"${filterOptions.researchType}"`);
      }

      // Apply advanced filters from research_parameters (same as main query)
      if (filterOptions.timeframe) {
        query = query.eq('research_parameters->>timeframe', filterOptions.timeframe);
      }

      if (filterOptions.country) {
        query = query.eq('research_parameters->>country', filterOptions.country);
      }

      if (filterOptions.sourceType) {
        query = query.eq('research_parameters->>source_type', filterOptions.sourceType);
      }

      // Apply type-specific filters (tech_domain, research_focus, etc.)
      if (filterOptions.typeSpecificFilters) {
        Object.entries(filterOptions.typeSpecificFilters).forEach(([key, value]) => {
          query = query.eq(`research_parameters->>${key}`, value);
        });
      }

      if (filterOptions.scope === 'recent') {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        query = query.gte('created_at', sevenDaysAgo.toISOString());
      }
      
      const { count, error } = await query;
      
      if (error) {
        console.error('Error getting private research count:', error);
        return 0;
      }
      
      return count || 0;
    }
  });

  // Delete mutation for private research
  const deleteResearchMutation = useMutation({
    mutationFn: async (researchId: string) => {
      console.log('[usePrivateResearchLibrary] Delete mutation started:', {
        researchId,
        userId: user?.id,
        timestamp: new Date().toISOString()
      });
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      // Use authenticated Supabase client with proper RLS context
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session found');
      }
      
      console.log('[usePrivateResearchLibrary] Deleting from database...');
      // Delete from database using RLS-aware context
      const { error: dbError } = await supabase
        .from('workbook_research')
        .delete()
        .eq('id', researchId)
        .eq('user_id', user.id); // Extra security check

      if (dbError) {
        console.error('[usePrivateResearchLibrary] Database deletion failed:', dbError);
        throw new Error(`Failed to delete research: ${dbError.message}`);
      }
      
      console.log('[usePrivateResearchLibrary] Universal storage cleanup automatic via CASCADE foreign key');
      
      console.log('[usePrivateResearchLibrary] Research deleted successfully:', researchId);
      return researchId;
    },
    onSuccess: (deletedResearchId) => {
      // Invalidate private research generator queries
      queryClient.invalidateQueries({ queryKey: ['private-research-generator'] });
      queryClient.invalidateQueries({ queryKey: ['private-research-generator-count'] });
      
      // Invalidate Project Research Library queries (since private research can appear in projects)
      queryClient.invalidateQueries({ queryKey: ['workbook-research'] });
      
      // Invalidate universal storage queries
      queryClient.invalidateQueries({ queryKey: ['universal-storage-items'] });
      queryClient.invalidateQueries({ queryKey: ['universal-library-data'] });
      
      toast({
        title: "Research deleted",
        description: "The research has been permanently removed from your private library.",
      });
    },
    onError: (error: Error) => {
      console.error('Delete research error:', error);
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete research. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Function to handle deleting research (unchanged)
  const handleDeleteResearch = useCallback(async (id: string) => {
    console.log('[usePrivateResearchLibrary] handleDeleteResearch called with ID:', {
      researchId: id,
      userId: user?.id,
      timestamp: new Date().toISOString()
    });
    
    if (!user?.id) {
      console.error('[usePrivateResearchLibrary] handleDeleteResearch - user not authenticated');
      return;
    }
    
    try {
      console.log('[usePrivateResearchLibrary] Calling deleteResearchMutation.mutateAsync with ID:', id);
      await deleteResearchMutation.mutateAsync(id);
      console.log('[usePrivateResearchLibrary] deleteResearchMutation completed successfully for ID:', id);
    } catch (error) {
      console.error('[usePrivateResearchLibrary] handleDeleteResearch failed for ID:', {
        researchId: id,
        error: error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  }, [user?.id, deleteResearchMutation]);

  return {
    research, // Filtered results
    totalUserResearch, // NEW: Total user research (for hasData calculation like Image Generator)
    isLoading,
    isError,
    loadMore: fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    handleDeleteResearch,
    isDeleting: deleteResearchMutation.isPending,
    searchTerm: debouncedSearchQuery, // Show the actual search term being used
    totalCount,
    // Additional return values for advanced filtering
    filterOptions, // Expose current filter state
    scope: filterOptions.scope, // Current scope
  };
}; 